import axios from 'axios';
import FileDownload from 'js-file-download';

import { resolveTheme, webSocketApiUrlsDictionary } from '../constants';
import doRequest from '../services/request';
import {
  AuditEvent,
  AuditQuery,
  EligibilityAccount,
  SharePartner,
  ShareToken,
  ShareTokenTtl,
  BindEdge,
  Connection,
  CrossTransaction,
  CrossTransactionQuery,
  CrossTransactionTotals,
  IntegrationStation,
  IntegrationStationDetail,
  IntegrationStationTotals,
  IntegrationToken,
  IntegrationTokenCreated,
  IntegrationTokenForm,
  InviteListItem,
  InviteQuery,
  InviteReview,
  SpringPage,
  GeneratedInvite,
  InviteForm,
  ScopeAccount,
  ScopeDelta,
  ScopeView,
  StationQuery,
} from '../types/responses/Interoperability';

/**
 * Chamadas do módulo Interoperabilidade (MV2-1712).
 *
 * Vai pelo gateway, como os demais microsserviços do dashboard — o
 * REACT_APP_API_URL aponta para o CS e não alcança o mk-ms-tenant-cross.
 *
 * O tenant vai no path porque a API recorta tudo por ele: nenhum endpoint
 * devolve contrato de terceiro.
 */

/**
 * Base das chamadas, resolvida a cada chamada e não no load do módulo.
 *
 * O useApp grava o `theme` dentro de um `.then()`, então no primeiro render ele
 * ainda está vazio. Resolvendo uma vez na importação, um tenant cujo tema está no
 * webSocketApiUrlsDictionary cairia no REACT_APP_API_GATEWAY_URL em vez do
 * gateway dele — é a armadilha de resolução de host documentada em constants.
 * O resolveTheme é o ponto único que aquele arquivo pede que se use.
 */
const baseUrl = (): string => {
  const gateway =
    webSocketApiUrlsDictionary[resolveTheme()]?.gatewayUrl ||
    process.env.REACT_APP_API_GATEWAY_URL;

  return `${gateway}/mk-ms-tenant-cross/tenants`;
};

const authHeaders = () => {
  const token = localStorage.getItem('auth-token');

  return {
    Platform: 'DASHBOARD',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/**
 * Quem está fazendo a ação, para a trilha de auditoria do contrato.
 *
 * A API aceita o ator como opcional e grava nulo quando não vem — foi o que
 * acontecia até aqui, e um histórico de contrato sem autor não serve para nada.
 * Prefere o e-mail; cai para nome e id porque o objeto do usuário logado varia
 * conforme o que o login devolveu, e um autor aproximado ainda vale mais que
 * nenhum.
 */
/**
 * Tira do filtro o que está vazio.
 *
 * String vazia num parâmetro de enum não é "sem filtro" para o Spring: ele tenta
 * converter e devolve 400.
 */
const withoutEmpty = (query: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(query).filter(
      ([, value]) => value !== '' && value !== null && value !== undefined,
    ),
  );

export const currentActor = (): string | undefined => {
  try {
    const user = JSON.parse(localStorage.getItem('auth-user') || 'null');

    return user?.email || user?.name || user?.id || undefined;
  } catch {
    return undefined;
  }
};

/** Cabeçalhos das operações que alteram contrato. */
const writeHeaders = () => {
  const actor = currentActor();

  return {
    ...authHeaders(),
    ...(actor ? { 'X-Actor': actor } : {}),
  };
};


/**
 * Querystring com array repetido: eventTypes=A&eventTypes=B.
 *
 * O axios 0.21 serializa array como `chave[]=valor`, que o binder do Spring não
 * liga numa List — o filtro de tipos seria silenciosamente ignorado. E a forma de
 * objeto do paramsSerializer só existe no axios 1.x.
 */
const repeatArrays = (params: Record<string, unknown>): string =>
  Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .flatMap(([key, value]) =>
      Array.isArray(value)
        ? value.map((item) => `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`)
        : [`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`],
    )
    .join('&');

/**
 * Tenant da sessão.
 *
 * A chave é `api-key`, gravada pelo useApp no boot a partir do header Api-Key,
 * com o REACT_APP_TENANT_KEY como reserva — é o mesmo valor que o
 * hooks/virtualgroup.ts usa como tenant_id. Não existe `tenant-id` no
 * localStorage: essa chave era invenção minha e devolvia nulo em sessão real,
 * derrubando todas as telas do módulo.
 */
export const currentTenantId = (): string | null =>
  localStorage.getItem('api-key') || process.env.REACT_APP_TENANT_KEY || null;

/**
 * Endereço que os integradores usam, sem o recorte por tenant.
 *
 * É o mesmo `baseUrl()` das chamadas do dashboard, menos o `/tenants` do fim: a API de
 * integração não leva tenant no caminho, porque o token já diz de quem é.
 *
 * Exposto porque a documentação da aba e a coleção do Postman precisam mostrar este endereço —
 * é a parte que o integrador não tem como adivinhar.
 */
export const integrationApiBaseUrl = (): string => baseUrl().replace(/\/tenants$/, '');

/**
 * Tamanho da página pedida ao servidor. Alto de propósito: o número de contratos de
 * uma conta é da ordem de dezenas, e uma requisição costuma bastar.
 */
const CONNECTIONS_PAGE_SIZE = 200;

/**
 * Todos os contratos da conta.
 *
 * O endpoint pagina (MV2-1820) e a tela precisa do conjunto inteiro: o filtro e o
 * paginador da aba Conexões trabalham sobre a lista carregada, o contador da aba diz
 * quantos existem, e as abas Estações, Transações e Histórico usam a mesma lista para
 * nomear o parceiro de cada linha. Filtrar sobre uma página só esconderia o que está
 * nas outras — é o engano que o listPaging avisa.
 *
 * Por isso o laço: pede a primeira página e continua enquanto o total disser que falta.
 * Não é o mesmo que ignorar a paginação — o corte existe e o cliente o percorre até o
 * fim, em vez de pedir um `size` enorme e torcer para caber.
 */
export const getConnections = async (tenantId: string): Promise<Connection[]> => {
  const fetchPage = async (page: number): Promise<SpringPage<Connection>> => {
    const { data } = await axios.get(`${baseUrl()}/${tenantId}/connections`, {
      headers: authHeaders(),
      params: { page, size: CONNECTIONS_PAGE_SIZE },
    });
    return data;
  };

  const first = await fetchPage(0);
  const rows = [...(first?.content || [])];

  for (let page = 1; page < (first?.totalPages || 0); page += 1) {
    // Sequencial de propósito: só chega aqui a conta com centenas de contratos, e
    // disparar as páginas todas de uma vez trocaria uma tela lenta por um pico de
    // requisições no gateway.
    // eslint-disable-next-line no-await-in-loop
    const next = await fetchPage(page);

    rows.push(...(next?.content || []));
  }

  return rows;
};

/** Contas da minha hierarquia, para a seleção de escopo do convite. */
export const getHierarchyAccounts = async (tenantId: string): Promise<ScopeAccount[]> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/binds/accounts`, {
    headers: authHeaders(),
  });
  return data;
};

/**
 * Hierarquia da conta (ela + descendentes) com can_tenant_bind e level, para a tela de
 * elegibilidade de interoperabilidade.
 *
 * Lida do CS (OCPP), e NÃO do cross como o resto do módulo: o flag can_tenant_bind é autoritativo
 * no CS e a conta sempre existe lá. Ler da réplica (cross) trazia lista vazia para conta ainda não
 * replicada — e como é esta lista que o botão de habilitar usa, não havia como a conta habilitar a
 * si mesma. Por isso vai pelo request padrão do dashboard (mesmo caminho da escrita), com a raiz
 * derivada da sessão no servidor. O CS recusa com 403 sem DASH_INTEROPERABILITY_MODERATE.
 */
export const getEligibility = async (): Promise<EligibilityAccount[]> => {
  const raw = await doRequest('tenants/eligibility', { method: 'GET' });

  if (!raw || typeof raw.ok !== 'boolean' || !raw.ok) {
    throw new Error('Falha ao carregar a elegibilidade.');
  }

  return raw.json();
};

/**
 * Liga/desliga can_tenant_bind em lote. A fonte de verdade do flag é o OCPP (CS), não o cross:
 * por isso a ESCRITA vai pelo request padrão do dashboard (API do CS, com Api-Key + sessão), e
 * não pelo gateway do cross como as demais chamadas do módulo. O CS grava e replica pro cross.
 * O servidor recusa com 403 se faltar a permissão DASH_INTEROPERABILITY_MODERATE.
 */
export const setCanTenantBind = async (tenantIds: string[], enabled: boolean): Promise<void> => {
  const raw = await doRequest('tenants/can-bind', {
    method: 'PUT',
    body: JSON.stringify({ tenant_ids: tenantIds, enabled }),
  });

  if (!raw || typeof raw.ok !== 'boolean' || !raw.ok) {
    throw new Error('Falha ao atualizar a elegibilidade.');
  }
};

/**
 * Espelha na trilha do módulo (cross) a moderação de elegibilidade, com o ator (X-Actor).
 * Chamado depois de o setCanTenantBind dar certo — o cross só guarda o evento; a fonte de
 * verdade do flag é o CS. Falha aqui não desfaz a escrita, então quem chama trata como best-effort.
 */
export const recordEligibilityAudit = async (
  tenantId: string,
  tenantIds: string[],
  enabled: boolean,
): Promise<void> => {
  await axios.post(
    `${baseUrl()}/${tenantId}/eligibility/audit`,
    { tenant_ids: tenantIds, enabled },
    { headers: { ...writeHeaders(), 'Content-Type': 'application/json' } },
  );
};


/**
 * Gera o convite. A resposta traz o token em claro — a única vez.
 *
 * writeHeaders, e não authHeaders: sem o X-Actor a trilha registrava "Convite
 * gerado" sem autor, e o "Gerado por" do histórico nascia vazio (MV2-1807).
 */
export const generateInvite = async (
  tenantId: string,
  form: InviteForm,
): Promise<GeneratedInvite> => {
  const { data } = await axios.post(`${baseUrl()}/${tenantId}/invites`, form, {
    headers: { ...writeHeaders(), 'Content-Type': 'application/json' },
  });
  return data;
};

export const getReceivedInvites = async (tenantId: string): Promise<InviteReview[]> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/invites/received`, {
    headers: authHeaders(),
  });
  return data;
};

/**
 * A árvore de escopo, com as SUBCONTAS paginadas (MV2-1812).
 *
 * O recorte é do servidor: antes a hierarquia inteira vinha numa resposta só e a
 * tela recortava dez linhas para desenhar, o que encolhia o painel sem encolher a
 * resposta.
 *
 * A matriz vem fora da página — nunca sai do contrato e aparece em todas.
 */
export const getScopeView = async (
  tenantId: string,
  partnerTenantId: string,
  page = 0,
  size = 10,
): Promise<ScopeView> => {
  const { data } = await axios.get(
    `${baseUrl()}/${tenantId}/binds/${partnerTenantId}/scope`,
    { headers: authHeaders(), params: { page, size } },
  );
  return data;
};

/**
 * Reescreve o escopo de um contrato ativo (MV2-1816).
 *
 * Manda o escopo DESEJADO inteiro, e não o que entra ou sai: o servidor compara
 * com o vigente e revoga ou reativa cada vínculo. Ele também reaplica as duas
 * regras do convite — a matriz nunca sai, e conta de fora da hierarquia não entra
 * —, então a tela não é a única guarda.
 *
 * writeHeaders porque alterar escopo é ação auditada (B4, CA7).
 */
export const changeContractScope = async (
  tenantId: string,
  partnerTenantId: string,
  accountsInScope: string[],
): Promise<void> => {
  await axios.put(
    `${baseUrl()}/${tenantId}/binds/${partnerTenantId}/scope`,
    accountsInScope,
    { headers: { ...writeHeaders(), 'Content-Type': 'application/json' } },
  );
};

/**
 * Manda só o que a pessoa mexeu no escopo (MV2-1812).
 *
 * PATCH, e não o PUT acima: com a árvore paginada a tela conhece apenas a página
 * que abriu, e mandar esse conjunto parcial como se fosse o escopo inteiro
 * apagaria as contas das páginas que ninguém viu.
 *
 * writeHeaders porque alterar escopo é ação auditada (B4, CA7).
 */
export const applyScopeDelta = async (
  tenantId: string,
  partnerTenantId: string,
  delta: ScopeDelta,
): Promise<void> => {
  await axios.patch(
    `${baseUrl()}/${tenantId}/binds/${partnerTenantId}/scope`,
    delta,
    { headers: { ...writeHeaders(), 'Content-Type': 'application/json' } },
  );
};

/**
 * Liga ou desliga a exibição das estações do parceiro no meu app.
 * Não altera o contrato: só esconde.
 */
export const setReceivedVisibility = async (
  tenantId: string,
  partnerTenantId: string,
  visible: boolean,
): Promise<void> => {
  await axios.put(
    `${baseUrl()}/${tenantId}/binds/${partnerTenantId}/received-visibility?visible=${visible}`,
    null,
    { headers: writeHeaders() },
  );
};

export const suspendContract = async (tenantId: string, partnerTenantId: string): Promise<void> => {
  await axios.post(`${baseUrl()}/${tenantId}/binds/${partnerTenantId}/suspend`, null, {
    headers: writeHeaders(),
  });
};

export const reactivateContract = async (tenantId: string, partnerTenantId: string): Promise<void> => {
  await axios.post(`${baseUrl()}/${tenantId}/binds/${partnerTenantId}/reactivate`, null, {
    headers: writeHeaders(),
  });
};

/**
 * Revoga o contrato inteiro. Não é reversível: reconectar exige novo convite.
 *
 * Verbo próprio, e não o DELETE do vínculo: aquele é da API antiga do serviço,
 * trabalha no par (source, target) e é chamado por outros sistemas.
 */
export const revokeContract = async (tenantId: string, partnerTenantId: string): Promise<void> => {
  await axios.post(`${baseUrl()}/${tenantId}/binds/${partnerTenantId}/revoke`, null, {
    headers: writeHeaders(),
  });
};

/**
 * Apaga de vez a conexão (o contrato inteiro), só quando já está toda revogada. Diferente do
 * revoke (soft): remove as linhas de bind. O servidor recusa com 400 se algum vínculo do
 * contrato ainda não estiver revogado.
 */
export const deleteConnection = async (tenantId: string, partnerTenantId: string): Promise<void> => {
  await axios.delete(`${baseUrl()}/${tenantId}/binds/${partnerTenantId}/contract`, {
    headers: writeHeaders(),
  });
};

export const getSentInvites = async (tenantId: string): Promise<InviteReview[]> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/invites/sent`, {
    headers: authHeaders(),
  });
  return data;
};

/**
 * Confere o token e devolve o resumo do contrato, sem aceitar nada.
 *
 * O token vai no corpo, nunca na URL: URL entra em histórico de navegador, em
 * log de proxy e no Referer. Recusa vem como erro do axios — quem chama traduz
 * com rejectionOf/rejectionMessage.
 */
export const validateInvite = async (
  tenantId: string,
  token: string,
): Promise<InviteReview> => {
  const { data } = await axios.post(
    `${baseUrl()}/${tenantId}/invites/validate`,
    { token },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
};

/** Aceita o convite. O token é de uso único: depois disto não vale mais. */
export const acceptInvite = async (
  tenantId: string,
  token: string,
  actor?: string,
): Promise<void> => {
  await axios.post(
    `${baseUrl()}/${tenantId}/invites/accept`,
    { token, ...(actor ? { actor } : {}) },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
};

/**
 * Recargas cross, paginadas.
 *
 * O tenant vai no path e a API recorta pela hierarquia dele: nunca devolve
 * recarga de estação de terceiro.
 */
export const getCrossTransactions = async (
  tenantId: string,
  query: CrossTransactionQuery,
  page: number,
  size: number,
): Promise<SpringPage<CrossTransaction>> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/cross-transactions`, {
    headers: authHeaders(),
    params: { ...query, page, size },
  });
  return data;
};

/** Totais do período filtrado. Consulta própria: somar a página daria outro número. */
export const getCrossTransactionTotals = async (
  tenantId: string,
  query: CrossTransactionQuery,
): Promise<CrossTransactionTotals> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/cross-transactions/totals`, {
    headers: authHeaders(),
    params: query,
  });
  return data;
};

/**
 * Baixa o CSV do período filtrado.
 *
 * Vai por axios, e não por link direto: o endpoint exige o Authorization, que um
 * <a href> não carrega. O arquivo é montado no servidor — o navegador não
 * remonta nada a partir da página aberta, que traria só a página.
 */
export const downloadCrossTransactionsCsv = async (
  tenantId: string,
  query: CrossTransactionQuery,
): Promise<void> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/cross-transactions/export`, {
    headers: { ...authHeaders(), accept: 'text/csv' },
    params: query,
    responseType: 'blob',
  });

  FileDownload(data, 'interoperabilidade-transacoes.csv');
};

/**
 * Trilha de auditoria do contrato, paginada.
 *
 * O filtro de tipos vai repetido na querystring (eventTypes=A&eventTypes=B), que
 * e como o Spring monta a List do form.
 */
/**
 * Convites em que este tenant é uma das partes, nos dois sentidos.
 *
 * Endpoint distinto de `/invites/sent` e `/invites/received`, que devolvem só os que
 * ainda valem porque alimentam o banner de pendentes. Aqui vêm também usado, expirado
 * e revogado — é o que a aba precisa para dizer o que aconteceu com cada convite.
 */
export const getInvites = async (
  tenantId: string,
  page: number,
  size: number,
  query: InviteQuery = {},
): Promise<SpringPage<InviteListItem>> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/invites`, {
    headers: authHeaders(),
    // Os filtros vão para o servidor porque esta listagem pagina lá: filtrar a página
    // carregada esconderia tudo o que está nas outras.
    params: { page, size, ...query },
  });
  return data;
};

/**
 * Revoga um convite que eu enviei.
 *
 * O servidor recusa com 403 quando quem chama não é quem convidou: revogar não é ato
 * do convidado. O ator vai no cabeçalho, para a trilha nomear quem praticou.
 */
export const revokeInvite = async (tenantId: string, inviteId: string): Promise<void> => {
  await axios.delete(`${baseUrl()}/${tenantId}/invites/${inviteId}`, {
    headers: { ...authHeaders(), 'X-Actor': currentActor() || '' },
  });
};

/**
 * Apaga de vez um convite já revogado (hard delete): remove a linha, diferente do revoke que só
 * muda o status. O servidor recusa com 400 se o convite não estiver revogado, e 403 se não foi
 * quem convidou. Sub-path /permanent porque o DELETE simples já é o revoke.
 */
export const deleteInvite = async (tenantId: string, inviteId: string): Promise<void> => {
  await axios.delete(`${baseUrl()}/${tenantId}/invites/${inviteId}/permanent`, {
    headers: writeHeaders(),
  });
};

/**
 * Aceita um convite já identificado, sem token.
 *
 * Quem chega pelo banner ou pela aba Convites tem o convite endereçado à própria
 * conta e está autenticado: pedir o token seria pedir para provar o que o recorte da
 * requisição já prova. E o token foi entregue fora do sistema — quem revisa por aqui
 * pode simplesmente não tê-lo em mãos.
 *
 * O servidor recusa com WRONG_TARGET quem não é o destinatário, então o caminho não é
 * mais frouxo que o do token; é só mais honesto sobre o que já se sabe.
 */
export const acceptInviteById = async (tenantId: string, inviteId: string): Promise<void> => {
  await axios.post(
    `${baseUrl()}/${tenantId}/invites/${inviteId}/accept`,
    {},
    { headers: { ...authHeaders(), 'X-Actor': currentActor() || '' } },
  );
};

/**
 * Tokens de integração da conta.
 *
 * Caminho do dashboard, e não o de integração: token de integrador não lista nem cria
 * tokens. As duas famílias de rota têm autenticação separada no gateway.
 */
export const getIntegrationTokens = async (tenantId: string): Promise<IntegrationToken[]> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/integration-tokens`, {
    headers: authHeaders(),
  });
  return data;
};

/**
 * Cria o token. A resposta traz o valor em claro, e é a única vez.
 *
 * Quem chama tem de exibir na hora: não existe caminho que o recupere depois.
 */
export const createIntegrationToken = async (
  tenantId: string,
  form: IntegrationTokenForm,
): Promise<IntegrationTokenCreated> => {
  const { data } = await axios.post(`${baseUrl()}/${tenantId}/integration-tokens`, form, {
    headers: { ...authHeaders(), 'X-Actor': currentActor() || '' },
  });
  return data;
};

/**
 * Troca o segredo do token, mantendo nome e escopos.
 *
 * A resposta traz o novo valor em claro, e é a única vez — igual à criação. O servidor
 * recusa renovar token revogado: ali o caminho é recriar.
 */
export const renewIntegrationToken = async (
  tenantId: string,
  tokenId: string,
): Promise<IntegrationTokenCreated> => {
  const { data } = await axios.post(
    `${baseUrl()}/${tenantId}/integration-tokens/${tokenId}/renew`,
    {},
    { headers: { ...authHeaders(), 'X-Actor': currentActor() || '' } },
  );
  return data;
};

/**
 * Estende a validade, com o mesmo segredo.
 *
 * Só vale para token vencido ou a vencer: ele não foi comprometido, apenas envelheceu.
 * `null` significa não vencer.
 */
export const extendIntegrationTokenValidity = async (
  tenantId: string,
  tokenId: string,
  expiresAt: string | null,
): Promise<void> => {
  await axios.put(
    `${baseUrl()}/${tenantId}/integration-tokens/${tokenId}/validity`,
    { expires_at: expiresAt },
    { headers: { ...authHeaders(), 'X-Actor': currentActor() || '' } },
  );
};

/**
 * Cria um token novo com o nome, o contato e os escopos de outro.
 *
 * É o caminho de volta de um revogado, sem devolver o segredo antigo — o revogado continua
 * revogado na lista, e o novo nasce ao lado.
 */
export const recreateIntegrationToken = async (
  tenantId: string,
  tokenId: string,
): Promise<IntegrationTokenCreated> => {
  const { data } = await axios.post(
    `${baseUrl()}/${tenantId}/integration-tokens/${tokenId}/recreate`,
    {},
    { headers: { ...authHeaders(), 'X-Actor': currentActor() || '' } },
  );
  return data;
};

/** Revoga. O servidor recusa com 403 quando o token é de outra conta. */
export const revokeIntegrationToken = async (
  tenantId: string,
  tokenId: string,
): Promise<void> => {
  await axios.delete(`${baseUrl()}/${tenantId}/integration-tokens/${tokenId}`, {
    headers: { ...authHeaders(), 'X-Actor': currentActor() || '' },
  });
};

/**
 * Apaga de vez um token já revogado (hard delete): remove a linha, diferente do revoke que só
 * marca revoked_at. O servidor recusa com 400 se o token não estiver revogado, e 403 se for de
 * outra conta. Sub-path /permanent porque o DELETE simples já é o revoke.
 */
export const deleteIntegrationToken = async (
  tenantId: string,
  tokenId: string,
): Promise<void> => {
  await axios.delete(`${baseUrl()}/${tenantId}/integration-tokens/${tokenId}/permanent`, {
    headers: writeHeaders(),
  });
};

export const getAuditEvents = async (
  tenantId: string,
  query: AuditQuery,
  page: number,
  size: number,
): Promise<SpringPage<AuditEvent>> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/bind-audit`, {
    headers: authHeaders(),
    params: { ...query, page, size },
    paramsSerializer: repeatArrays,
  });
  return data;
};

/**
 * Estações disponíveis para as integrações (MV2-1706).
 *
 * Os filtros vazios são retirados antes de sair: mandar `issue=` faz o Spring
 * tentar converter string vazia no enum e devolver 400.
 */
export const getIntegrationStations = async (
  tenantId: string,
  query: StationQuery,
  page: number,
  size: number,
): Promise<SpringPage<IntegrationStation>> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/integration-stations`, {
    headers: authHeaders(),
    params: { ...withoutEmpty(query), page, size },
  });
  return data;
};

/**
 * O detalhe de uma estação, com os conectores.
 *
 * Chamada própria, na abertura do painel: a listagem não carrega conector de estação
 * que ninguém vai abrir.
 */
export const getIntegrationStationDetail = async (
  tenantId: string,
  chargeBoxId: string,
): Promise<IntegrationStationDetail> => {
  const { data } = await axios.get(
    `${baseUrl()}/${tenantId}/integration-stations/${chargeBoxId}`,
    { headers: authHeaders() },
  );
  return data;
};

/** Os números do filtro inteiro, para o cabeçalho da aba. */
export const getIntegrationStationTotals = async (
  tenantId: string,
  query: StationQuery,
): Promise<IntegrationStationTotals> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/integration-stations/totals`, {
    headers: authHeaders(),
    params: withoutEmpty(query),
  });
  return data;
};

/** Baixa a trilha filtrada. Mesmo motivo do CSV de transações: precisa do token. */
export const downloadAuditCsv = async (tenantId: string, query: AuditQuery): Promise<void> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/bind-audit/export`, {
    headers: { ...authHeaders(), accept: 'text/csv' },
    params: query,
    paramsSerializer: repeatArrays,
    responseType: 'blob',
  });

  FileDownload(data, 'interoperabilidade-historico.csv');
};

/**
 * Ligações do mapa: um vínculo por linha, nos dois sentidos.
 *
 * Uma requisição só. Pedir o escopo de cada parceiro daria N+1 no cliente — uma
 * chamada por parceiro desenhado.
 */
export const getBindEdges = async (tenantId: string): Promise<BindEdge[]> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/binds/map`, {
    headers: authHeaders(),
  });
  return data;
};

/** O token da conta, ou nulo se ela nunca gerou um. */
export const getShareToken = async (tenantId: string): Promise<ShareToken | null> => {
  const { data } = await axios.get(`${baseUrl()}/${tenantId}/share-token`, {
    headers: authHeaders(),
  });
  return data || null;
};

/**
 * Gera o token da conta, ou o renova.
 *
 * Renovar invalida o anterior: quem o tinha perde a capacidade de convidar esta
 * conta. Vai com X-Actor porque a renovação entra na trilha de auditoria.
 */
export const generateShareToken = async (
  tenantId: string,
  ttl: ShareTokenTtl,
): Promise<ShareToken> => {
  const { data } = await axios.post(
    `${baseUrl()}/${tenantId}/share-token`,
    { ttl },
    { headers: { ...writeHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
};

/**
 * Descobre de quem e um token de conta, antes de gerar o convite.
 *
 * POST com o token no corpo, e nao GET com ele na querystring: URL entra em
 * historico de navegador, log de proxy e Referer.
 */
export const resolveSharePartner = async (
  tenantId: string,
  token: string,
): Promise<SharePartner> => {
  const { data } = await axios.post(
    `${baseUrl()}/${tenantId}/share-token/resolve`,
    { token },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return data;
};
